import { Button, Divider, MenuItem, Stack, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { Iconify, RHFTextField, RHFDatePicker, RHFMultiSelect } from '@app/components';
import useResponsive from '@app/hooks/use-responsive';
import { Strings } from '@app/constants';
import { AddRoleDialog, selectRoleState } from '@app/modules/role';
import { selectDesignationState, AddDesignation } from '@app/modules/designation';
import { selectDepartmentState, AddDepartment } from '@app/modules/departments';
import { selectEmployeesState } from '@app/modules/employees';
import { useDialog } from '@app/hooks';

// --------------------------------------------

interface props {
  setOfficialAddress?: (value: string) => void;
  disabled?: boolean;
  joiningDate?: string;
  roleDisabled?: boolean; // props to disable role
  hideCandidateAndIntern?: boolean;
  title?: string;
}

const OfficialInfoForm = ({
  setOfficialAddress,
  disabled,
  joiningDate,
  roleDisabled,
  hideCandidateAndIntern,
  title,
}: props) => {
  const isMobile = useResponsive('up', 'sm');
  const direction = isMobile ? 'row' : 'column';

  const firstName: string = useWatch({ name: 'personalInfo.firstName' });
  const lastName: string = useWatch({ name: 'personalInfo.lastName' });
  const officialEmailValue = `${firstName}${lastName}@syncglob.com`;

  useEffect(() => {
    if (setOfficialAddress) {
      setOfficialAddress(officialEmailValue);
    }
  }, [officialEmailValue, setOfficialAddress]);

  const roleState = useSelector(selectRoleState);
  const designationState = useSelector(selectDesignationState);
  const departmentState = useSelector(selectDepartmentState);
  const managerState = useSelector(selectEmployeesState);

  const addDesignationDialog = useDialog();
  const addDepartmentDialog = useDialog();
  const addRoleDialog = useDialog();

  const managerData = (managerState.data?.records ?? [])
    .filter((option) => option.roleId === 1 || option.roleId === 2)
    .map((option) => ({ label: option.firstName, value: option.id }));
  return (
    <>
      <Stack spacing={2} pb={2}>
        <Stack>
          <Typography variant="h5">{Strings.official.official}</Typography>
        </Stack>
        <Divider />
        <Stack gap={3}>
          <Stack direction={direction} spacing={2}>
            <Stack flex={1}>
              <RHFTextField
                name="officialInfo.officialEmail"
                label={Strings.field.officialEmail}
                fullWidth
                required
                disabled={disabled}
              />
            </Stack>
            <Stack flex={1}>
              <RHFDatePicker
                name="officialInfo.joiningDate"
                required
                label={Strings.official.joining}
                defaultValue={joiningDate ? dayjs(joiningDate, 'DD/MM/YYYY') : null}
                disabled={disabled}
              />
            </Stack>
            <Stack direction={'row'} flex={1} gap={1}>
              <RHFTextField
                name="officialInfo.roleId"
                label={Strings.role.role}
                select
                fullWidth
                required
                disabled={(title === 'intern' || roleDisabled) ?? disabled}
              >
                {roleState.data?.records?.map(
                  (option) =>
                    option.active &&
                    (!hideCandidateAndIntern || (option.id !== 3 && option.id !== 4)) && (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    )
                )}
              </RHFTextField>
              <Stack alignSelf={'center'}>
                <Button
                  variant="outlined"
                  color="inherit"
                  disabled={disabled}
                  sx={{
                    borderRadius: '8px',
                    width: '55px',
                    height: '55px',
                    minWidth: '40px',
                    mb: 2,
                  }}
                  onClick={addRoleDialog.show}
                >
                  <Iconify icon="ic:outline-plus" />
                </Button>
              </Stack>
            </Stack>
          </Stack>
          <Stack direction={direction} spacing={2}>
            <Stack direction={'row'} flex={1} gap={1}>
              <RHFTextField
                name="officialInfo.designation"
                label={Strings.designation.designation}
                required
                select
                disabled={disabled}
              >
                {designationState.data?.records?.map((option) => (
                  <MenuItem key={option.id} value={option.name}>
                    {option.name}
                  </MenuItem>
                ))}
              </RHFTextField>
              <Stack alignSelf={'center'}>
                <Button
                  variant="outlined"
                  color="inherit"
                  disabled={disabled}
                  sx={{
                    borderRadius: '8px',
                    width: '55px',
                    height: '55px',
                    minWidth: '40px',
                    mb: 2,
                  }}
                  onClick={addDesignationDialog.show}
                >
                  <Iconify icon="ic:outline-plus" />
                </Button>
              </Stack>
            </Stack>
            <Stack direction={'row'} flex={1} gap={1}>
              <RHFTextField
                name="officialInfo.department"
                label={Strings.department.department}
                required
                select
                disabled={disabled}
              >
                {departmentState.data?.records?.map((option) => (
                  <MenuItem key={option.id} value={option.name}>
                    {option.name}
                  </MenuItem>
                ))}
              </RHFTextField>
              <Stack alignSelf={'center'}>
                <Button
                  variant="outlined"
                  color="inherit"
                  disabled={disabled}
                  sx={{
                    borderRadius: '8px',
                    width: '55px',
                    height: '55px',
                    minWidth: '40px',
                    mb: 2,
                  }}
                  onClick={addDepartmentDialog.show}
                >
                  <Iconify icon="ic:outline-plus" />
                </Button>
              </Stack>
            </Stack>
            <Stack flex={1}>
              <RHFMultiSelect
                label={Strings.addEmployee.manager}
                disabled={disabled}
                name="officialInfo.managerId"
                required
                options={managerData}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      {addDesignationDialog.visible && (
        <AddDesignation open={addDesignationDialog.visible} onClose={addDesignationDialog.hide} />
      )}
      {addDepartmentDialog.visible && (
        <AddDepartment open={addDepartmentDialog.visible} onClose={addDepartmentDialog.hide} />
      )}
      {addRoleDialog.visible && (
        <AddRoleDialog open={addRoleDialog.visible} onClose={addRoleDialog.hide} />
      )}
    </>
  );
};

export { OfficialInfoForm };
